<template>
  <cs-preloader-overlay :loading="loading">
    <div :class="$style.container">

      <div :class="$style.title">
        <desktop-capusta-title v-if="isDesktop" :title="$t('common.payout')" />
        <mobile-capusta-title v-else :title="$t('common.payout')" />
      </div>

      <div :class="$style.header">
        <payout-header
          :balance="displayBalance"
          :available="displayAvailableForPayout"
        />
      </div>

      <div :class="$style.content">
        <substrate-mount show-tail>
          <ul
            v-if="!payoutPermissionIsEnabled"
            v-cursor:pointer
            class="cards"
            @click.capture="$router.push({ name: 'cardSelect' })"
          >
            <selectable-card-panel :card="getLinkedCard">
              <template v-slot:action="{ disabled }">
                <simple-button
                  size="small"
                  variant="darken"
                  :disabled="disabled"
                >
                  {{ $t('common.change') }}
                </simple-button>
              </template>

              <template
                v-if="linkedCardLimits"
                v-slot:additional
              >
                <payout-limits
                  :currency="currency"
                  :limits="linkedCardLimits"
                  :loading="loading"
                  is-notifying
                />
              </template>
            </selectable-card-panel>
          </ul>

          <input-card-pan
            v-else
            ref="pan"
            v-model="pan"
            input-id="input-pan"
            :input-name="$t('card.number')"
            @focusNext="focusPrice"
          />

          <div v-if="!payoutPermissionIsEnabled" class="switcher-container">
            <cs-switcher
              v-model="autoPayout"
              :label="$t('automatic.daily.payout')"
              @input="handleChangeAutoPayout"
            />
          </div>

          <template v-if="autoPayout">
            <p
              :class="$style.text"
              v-html="$t('all.available.amount.to.card.every.day', { displayMaxPayoutAmount })"
            ></p>
          </template>

          <template v-else>
            <input-price
              ref="amount"
              v-model="amount"
              input-id="input-withdrawal-amount"
              :input-name="$t('transfer.to.card', { getCurrencySign: currency.sign })"
              :input-title="$t('enter.amount')"
              :min-value="commissionData.minPayoutAmount"
              :max-value="getMaxAvailableAmount"
              :commission="commissionData.commission"
              :currency="currency"
              required
              @change="amount = $event"
            />

            <template v-if="isExternalCheckoutProvider">
              <div v-if="!isInvalidAmount" class="commission">

                <div class="commission-text">
                  <div class="commission-text-content">
                    <span>
                      {{ dealIds.length > 0 ? $t('common.commission') : $t('commission.value.after.payout.amount') }}
                    </span>
                    <div v-if="dealIds.length > 0 && !commissionLoading" class="commission-currency">
                      <span>
                        {{ displayCommissionAmount }}
                      </span>
                    </div>
                  </div>
                </div>

                <div v-if="commissionLoading" class="commission-loader">
                  <loader-dots />
                </div>
              </div>
            </template>

            <div v-else-if="!payoutPermissionIsEnabled || isPanDetected" class="commission">

              <div class="commission-text">
                <div class="commission-text-title">
                  {{ $t('common.commission') }}
                </div>

                <div v-if="!commissionLoading && commissionData.promoDetails" class="commission-text-content">
                  <span v-html="getCommissionText(1)"></span>

                  <div v-if="!commissionLoading && isAmount" class="commission-currency">
                    <span v-if="commissionData.promoDetails">
                      {{ displayCommissionAmount }}
                    </span>
                  </div>
                </div>

                <div
                  v-if="commissionData.promoDetails && +preparedAmount > commissionData.promoDetails.remain / 100 && isAmount"
                  class="commission-text-content"
                >
                  <span v-html="getCommissionText(2)"></span>

                  <div v-if="!commissionLoading && isAmount" class="commission-currency">
                    <span v-if="commissionData.promoDetails && +preparedAmount > commissionData.promoDetails.remain / 100">
                      + {{ displayPromoDetailsCommission }}
                    </span>
                  </div>
                </div>

                <div v-if="!commissionData.promoDetails" class="commission-text-content">
                  <span v-html="getCommissionText(0)"></span>

                  <div v-if="!commissionLoading && isAmount" class="commission-currency">
                    <span v-if="!commissionData.promoDetails && commissionData.commission">
                      {{ displayCommissionAmount }}
                    </span>
                  </div>
                </div>
              </div>

              <div v-if="commissionLoading" class="commission-loader">
                <loader-dots />
              </div>
            </div>
          </template>
        </substrate-mount>

        <template v-if="!autoPayout">

          <template v-if="isExternalCheckoutProvider">
            <div v-if="isInvalidAmount && !isInvalidMinimalAmount" class="payoutInfo">
              <div>
                {{ $t('amount.cant.be.payout') }} &mdash; <span>{{ displayAmount }}</span>
              </div>
              <div>
                <a @click="getMinAvailableAmountByDeals">
                  {{ $t('reduce.to.closest') }}
                </a>
              </div>
            </div>

            <div v-else class="submit-button-container">
              <cs-button width="100%" :disabled="!isValid" @click="handlePayout">
                {{ $t('to.transfer') }}
              </cs-button>
            </div>
          </template>

          <template v-else>
            <div v-if="isShownReduceAmountButton" class="payoutInfo">
              <div v-html="displayErrorMessage"></div>
              <div v-if="!isEmptyPayoutCount && !isEmptyPayoutAmount" class="payout-less-container">
                <p>
                  <a @click="calculateMaximumAmount">{{ $t('reduce.amount') }}</a>
                </p>
              </div>
            </div>
            <div v-else class="submit-button-container">
              <div id="buttonPayout">
                <button class="button" :disabled="!isValid" @click.prevent="handlePayout">
                  <span>{{ displayPayoutButtonText }}</span>
                  <p>({{ $t('with.commission') }})</p>
                </button>
              </div>
            </div>
          </template>
        </template>
      </div>
    </div>

    <cs-dialog v-model="isShownErrorPayout">
      <dialog-default-layout>
        <div slot="title" class="error-title">
          <h2>{{ $t('payout.error') }}</h2>
        </div>
        <div slot="content" class="error-message">
          <p>{{ $t('use.another.card.or.wait') }}</p>
        </div>
        <div slot="footer" class="error-controls">
          <div class="error-control-active">
            <cs-button width="100%" @click="handleChangeCart">
              {{ $t('choose.another') }}
            </cs-button>
          </div>
          <div class="error-control-cancel">
            <cs-button width="100%" type="transparent" @click="isShownErrorPayout = false">
              {{ $t('common.close') }}
            </cs-button>
          </div>
        </div>
      </dialog-default-layout>
    </cs-dialog>
  </cs-preloader-overlay>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { isBetaTest } from 'lib/system/env';

import { formatAmount, normalizeAmount } from 'lib/utils/amount';
import throttle from 'lib/utils/throttle';

import HTTPService from '@/services/Http';
import logrocket from '@/services/logrocket';

import SimpleButton from 'molecules/common/SimpleButton';
import MobileCapustaTitle from 'molecules/mobile/CapustaTitle';
import DesktopCapustaTitle from 'molecules/desktop/CapustaTitle';

import SubstrateMount from '@/components/capusta/SubstrateMount';
import InputCardPan from '@/components/cardElements/InputCardPan';
import InputPrice from '@/components/inputElements/InputPrice';
import LoaderDots from '@/components/otherElements/LoaderDots';

import PayoutHeader from 'organisms/payout/PayoutHeader';
import PayoutLimits from 'organisms/payout/components/PayoutLimits';
import SelectableCardPanel from 'organisms/card/SelectableCardPanel';

import DialogDefaultLayout from 'modules/Dialogs/DefaultLayout';

export default {
  name: 'PayoutCard',
  components: {
    SimpleButton,
    PayoutHeader,
    MobileCapustaTitle,
    DesktopCapustaTitle,
    SubstrateMount,
    InputCardPan,
    InputPrice,
    LoaderDots,
    SelectableCardPanel,
    PayoutLimits,
    DialogDefaultLayout,
  },
  created() {
    this.initialize();
  },
  mounted() {
    this.autoFill();
    this.checkAutoPayout();
  },
  data() {
    return {
      loading: false,
      amount: '',
      pan: '',
      commissionData: {
        commission: 0,
        commissionPercent: 0,
        minimalCommission: 0,
        maxPayoutAmount: 0,
        minPayoutAmount: 0,
        alpha_2: '',
        promoDetails: null,
      },
      commissionLoading: false,
      autoPayout: false,
      isBetaTest: isBetaTest(),

      dealIds: [],
      isShownErrorPayout: false,
      isInvalidAmount: false,
      isInvalidMinimalAmount: false,
    };
  },
  computed: {
    currency() {
      return this.PROJECT_INFO.currency;
    },
    preparedPan() {
      return this.pan.replace(/\s/g, '');
    },
    isPanDetected() {
      return this.preparedPan.length >= 6;
    },
    numberOrCardId() {
      return this.payoutPermissionIsEnabled ? this.preparedPan : this.getLinkedCard.id;
    },
    preparedAmount() {
      return this.amount.replace(/\s/g, '');
    },
    formattedAmount() {
      return Math.round(this.preparedAmount * 100);
    },
    isAmount() {
      return this.formattedAmount > 0;
    },
    requestData() {
      let params = '';

      if (this.$validate.isNotEmptyArray(this.dealIds)) {
        params += `deal_ids=${this.dealIds.join(',')}`;
      }

      if (!this.payoutPermissionIsEnabled) {
        return params;
      }

      if (this.$validate.isNotEmptyString(this.preparedPan)) {
        params += `pan=${this.preparedPan}`;
      }

      return params;
    },
    getCommission() {
      const { promoDetails, commission } = this.commissionData;

      if (this.$validate.isNull(promoDetails)) {
        return commission;
      }

      return promoDetails.addedCommission || 0;
    },
    getMinimalComission() {
      const { promoDetails, minimalCommission } = this.commissionData;

      if (this.$validate.isNull(promoDetails)) {
        return formatAmount(minimalCommission, {
          significant_zeros: this.currency.significant_zeros,
          type: this.currency.type,
        });
      }

      return formatAmount(promoDetails.addedMinimalCommission, {
        significant_zeros: this.currency.significant_zeros,
        type: this.currency.type,
      });
    },
    getCountryText() {
      return this.commissionData && this.getAlpha2(this.commissionData.alpha_2)
        ? this.getAlpha2(this.commissionData.alpha_2) + ' – '
        : '';
    },
    isBalanceIsLower() {
      const { availableForPayout } = this.PROJECT_INFO;
      const { minPayoutAmount } = this.commissionData;

      return availableForPayout < minPayoutAmount || availableForPayout < this.formattedAmount;
    },
    getMaxAvailableAmount() {
      return this.commissionData.maxAvailableAmount + this.getCommission;
    },
    // eslint-disable-next-line complexity
    isValid() {
      // мерчант не прошел обязательную иденификацию
      if (this.isNeedIdentification && !this.isApproved) {
        return false;
      }

      // для данного мерчанта вывод запрещен (только под бета тестом)
      if (this.isBetaTest && this.projectPayoutsDisabled) {
        return false;
      }

      // баланс проекта отрицательный>
      if (!this.PROJECT_INFO.availableForPayout) {
        return false;
      }

      // если превышен лимит на количество выводов в месяц
      if (this.isEmptyPayoutCount) {
        return false;
      }

      // только для провайдера юкасса
      if (this.isExternalCheckoutProvider) {
        return this.$validate.isNotEmptyString(this.amount) && this.$validate.isNotEmptyArray(this.dealIds);
      }

      // есть разрешение для вывода на карты
      if (this.payoutPermissionIsEnabled) {
        return !!this.amount.length && !this.commissionLoading && !this.$refs.amount?.$v.$error && this.$refs.pan.cardIsValid;
      }

      return !!this.amount.length && !this.commissionLoading && this.getLinkedCard.id && !this.$refs.amount?.$v.$error;
    },
    isShownReduceAmountButton() {
      // веденная сумма > суммы на балансе
      if (this.formattedAmount > this.PROJECT_INFO.availableForPayout) {
        return true;
      }

      // отсутствует доступная для вывода сумма или количество попыток
      if (this.isEmptyPayoutCount || this.isEmptyPayoutAmount) {
        return true;
      }

      // введенная сумма + комиссия > остатка по месячному лимиту
      if (this.getTotalPayoutSum > this.commissionData.monthlyAvailablePayoutAmount) {
        return true;
      }

      // введенная сумма < минимальной суммы вывода
      if (this.formattedAmount < (this.commissionData.minPayoutAmount + this.commissionData.commission)) {
        return false;
      }

      // введенная сумма + комиссия > максимальная сумма на вывод и она входит в месячный лимит
      if (this.getTotalPayoutSum > this.getMaxAvailableAmount) {
        return this.getTotalPayoutSum <= this.commissionData.monthlyPayoutAmount;
      }

      return false;
    },
    getTotalPayoutSum() {
      return this.isAmount ? this.formattedAmount + this.getCommission : 0;
    },
    displayErrorMessage() {
      // Сумма недоступна для вывода.
      if (this.isBalanceIsLower) {
        return this.$t('not.enough.funds');
      }

      // недостаточно средств по месячному лимиту
      if (this.isEmptyPayoutAmount) {
        return this.$t('payout.monthly.available.limit', {
          amount: this.displayMonthlyPayoutAmount,
          link: '/cardselect',
        });
      }

      // достингут лимит количенства выводов по месячному лимиту
      if (this.isEmptyPayoutCount) {
        return this.$t('payout.count.available.limit', {
          count: this.displayPayoutCount,
          link: '/cardselect',
        });
      }

      // введенная сумма + комиссия > максимальная сумма на вывод и она входит в месячный лимит
      if (this.getTotalPayoutSum > this.getMaxAvailableAmount) {
        if (this.getTotalPayoutSum > this.commissionData.maxPayoutAmount) {
          // Выше максимальной суммы на вывод.
          return this.$t('more.then.maximum.amount.for.payout');
        }

        // введенная сумма + комиссия > остатка по месячному лимиту
        if (this.getTotalPayoutSum > this.commissionData.monthlyAvailablePayoutAmount) {
          return this.$t('payout.monthly.available.limit', {
            amount: this.displayMonthlyPayoutAmount,
            link: '/cardselect',
          });
        }
      }

      // Выше максимальной суммы на вывод.
      return this.$t('more.then.maximum.amount.for.payout');
    },
    displayAmount() {
      return formatAmount(this.formattedAmount, this.currency);
    },
    displayPayoutButtonText() {
      return this.$t('payout.amount', {
        displayAmount: formatAmount(this.getTotalPayoutSum, this.currency),
      });
    },
    displayCommissionAmount() {
      return formatAmount(this.commissionData.commission, this.currency);
    },
    displayMaxPayoutAmount() {
      return formatAmount(this.commissionData.maxPayoutAmount, this.currency);
    },
    displayPromoDetailsCommission() {
      const { promoDetails } = this.commissionData;

      if (this.$validate.isNull(promoDetails)) {
        return '';
      }

      return formatAmount(this.commissionData.promoDetails?.addedCommission, this.currency);
    },
    displayMonthlyPayoutAmount() {
      return formatAmount(this.commissionData.monthlyPayoutAmount, this.currency);
    },
    displayPayoutCount() {
      if (!this.linkedCardLimits?.limited) {
        return null;
      }

      return this.linkedCardLimits.payoutCountLimit;
    },
    displayBalance() {
      const { balance, currency } = this.PROJECT_INFO;

      return formatAmount(balance, currency);
    },
    displayAvailableForPayout() {
      const { availableForPayout, currency } = this.PROJECT_INFO;

      return formatAmount(availableForPayout, currency);
    },
    isEmptyPayoutCount() {
      if (!this.linkedCardLimits?.limited) {
        return false;
      }

      return this.linkedCardLimits?.payoutCountActual === this.linkedCardLimits?.payoutCountLimit;
    },
    isEmptyPayoutAmount() {
      return this.linkedCardLimits?.payoutSumLeft === 0;
    },
    ...mapState(['PROJECT_INFO']),
    ...mapGetters([
      'isDesktop',
      'payoutPermissionIsEnabled',
      'isNeedIdentification',
      'projectPayoutsDisabled',
      'isExternalCheckoutProvider',
    ]),
    ...mapGetters('identity', ['isApproved']),
    ...mapGetters('cards', [
      'getLinkedCard',
      'isEmptyCardList',
      'isNoLinkedCards',
      'isSingleAvailableCard',
      'isLinkedCardIsExpired',
      'isLinkedCardInBlacklisted',
      'linkedCardLimits',
    ]),
  },
  watch: {
    amount() {
      this.loadCommissionThrottle();
    },
    pan() {
      this.loadCommissionThrottle();
    },
    dealIds() {
      this.loadCommissionThrottle();
    },
  },
  methods: {
    async initialize() {
      this.loading = true;

      await Promise.all([
        this.fetchCardList(),
        this.fetchCardLimits(),
        this.loadCommission(),
      ]);

      if (this.payoutPermissionIsEnabled) {
        console.log('Разрешение на выплаты без привяки карты включено');

        this.loading = false;
        return;
      }

      console.log('Разрешение на выплаты без привяки карты отключено');

      if (this.isEmptyCardList) {
        console.log('Привязанных карт не обнаружено');

        this.$router.push({ name: 'card.binding' });
        return;
      }

      if (this.isNoLinkedCards) {
        console.log('Карта на вывод не установлена', 'отправить на выбор другой карты');

        this.$router.push({ name: 'cardSelect' });
        return;
      }

      if (this.isLinkedCardIsExpired || this.isLinkedCardInBlacklisted) {
        console.log('Карта на вывод просрочена или в черном списке');

        if (this.isSingleAvailableCard) {
          console.log('Это единственная карта на вывод', 'отправить на привязку новой карты');

          this.$router.push({ name: 'card.binding' });
        } else {
          console.log('Есть еще карты на вывод', 'отправить на выбор другой карты');

          this.$router.push({ name: 'cardSelect' });
        }

        return;
      }

      console.log('Карта на вывод корректна', 'разрешен доступ на вывод средств');
      this.loading = false;
    },
    focusPrice() {
      document.getElementById('input-withdrawal-amount').focus();
    },
    getFormattedPrice(rawPrice) {
      const preparedPrice = (rawPrice / 100).toFixed(2);

      return this.formatPrice(preparedPrice, ' ');
    },
    getAlpha2(alpha) {
      const countryMap = {
        'RU': this.$t('common.russia'),
        'UA': 'UA',
      };

      if (alpha === null) {
        return this.$t('common.cis');
      }

      return countryMap[alpha];
    },
    checkAutoPayout() {
      this.autoPayout = this.PROJECT_INFO.autoPayout === 'ENABLED';
    },
    autoFill() {
      if (!this.$validate.isObjectWithFields(this.$route.params, ['transaction'])) {
        return false;
      }

      if (!this.$validate.isObjectWithFields(this.$route.params.transaction, ['amount'])) {
        return false;
      }

      this.amount = formatAmount(this.$route.params.transaction.amount, {
        significant_zeros: this.currency.significant_zeros,
        type: this.currency.type,
      });

      this.$refs.amount?.$v.$touch();
    },
    // eslint-disable-next-line prefer-arrow-callback
    loadCommissionThrottle: throttle(function () {
      this.loadCommission();
    }, 500),
    loadCommission() {
      // Получение комиссии при выводе по номмеру карты возможно при наличии минимум 6 цифр номера карты
      if (this.payoutPermissionIsEnabled && this.preparedPan.length < 6) {
        return;
      }

      this.commissionLoading = true;

      HTTPService.getCommission(this.formattedAmount, this.requestData)
        .then(response => {
          this.commissionData = response.data;
        })
        .catch(error => {
          logrocket.logger(error);
        })
        .finally(() => {
          this.commissionLoading = false;
        });
    },
    handlePayout() {
      if (!this.isValid) {
        this.$alert({
          type: 'warning',
          text: 'mistakes.in.form',
        });
        return;
      }

      const parameterName = this.payoutPermissionIsEnabled ? 'account' : 'cardId';

      let payoutRequestData = {
        currency: this.PROJECT_INFO.currency,
        amount: this.formattedAmount,
        [parameterName]: this.numberOrCardId,
      };

      if (this.$validate.isNotEmptyArray(this.dealIds)) {
        payoutRequestData.dealIds = this.dealIds;
      }

      this.commissionLoading = true;

      HTTPService.payOut(payoutRequestData)
        .then(() => {
          this.$alert({
            type: 'cabbage',
            text: 'money.transfered.to.card',
          });

          this.$router.push({ name: 'cabinet' });
        })
        .catch(error => {
          logrocket.logger(error);

          console.log('error', error);
          const { status } = error.data;

          if (error.status === 400 && status === 'PS_DECLINE') {
            this.isShownErrorPayout = true;
            return;
          }

          if (error.status === 400 && status === 'TRANSACTION_LIMITS_EXCEEDED') {
            this.$alert({
              type: 'warning',
              text: 'payout.card.limits.error',
            });
            return;
          }

          this.$alert({
            type: 'warning',
            text: 'server.error',
          });
        })
        .finally(() => {
          this.commissionLoading = false;
        });
    },
    getCommissionText(promo) {
      const { promoDetails, commissionPercent } = this.commissionData;

      switch (promo) {
        case 1:
          // промо первая строка
          if (this.$validate.isNull(promoDetails)) {
            return '';
          }

          // eslint-disable-next-line no-case-declarations
          const remainText = promoDetails.remain
            ? `, ${this.$t('common.amount.to', {
              displayAmount: `${promoDetails.remain / 100} ${this.currency.sign}`,
            })}`
            : '';

          return `
            ${this.getCountryText}
            ${commissionPercent}%
            ${remainText}
          `;
        case 2:
          // промо вторая строка строка
          return `
            ${this.getCountryText}
            ${promoDetails.addedCommissionPercent}%, ${this.$t('but.not.less')} ${this.getMinimalComission}
          `;
        case 0:
          // промо нет
          return `
            ${this.getCountryText}
            ${commissionPercent}%, ${this.$t('but.not.less')} ${this.getMinimalComission}
          `;
        default:
          return '';
      }
    },
    calculateMaximumAmount() {
      HTTPService.getCommission(this.commissionData.maxAvailableAmount, this.requestData)
        .then(response => {
          this.commissionData = response.data;

          const amount = formatAmount(this.commissionData.maxAvailableAmount, {
            significant_zeros: this.currency.significant_zeros,
            type: this.currency.type,
          });
          this.amount = parseFloat(amount) > 0 ? amount : '';
        })
        .catch(error => {
          logrocket.logger(error);
        });
    },

    async getMinAvailableAmountByDeals() {
      if (this.$validate.isEmptyString(this.amount)) {
        return;
      }

      const { data } = await HTTPService.getPayoutSums(this.formattedAmount);
      this.amount = formatAmount(data.minAmount, {
        significant_zeros: this.currency.significant_zeros,
        type: this.currency.type,
      });
    },

    handleChangeAutoPayout() {
      if (!this.$validate.isObjectWithFields(this.getLinkedCard, ['id'])) {
        return;
      }

      const isChecked = this.autoPayout;
      const { projectId } = this.PROJECT_INFO;

      const preparedProject = Object.assign(
        {},
        this.PROJECT_INFO,
        { autoPayout: isChecked ? 'ENABLED' : 'DISABLED' }
      );

      HTTPService.editProject(preparedProject, projectId)
        .then(() => {
          this.$alert({
            type: 'cabbage',
            text: isChecked ? 'common.autopayout.on' : 'common.autopayout.off',
          });
        })
        .catch(error => {
          logrocket.logger(error);

          this.$alert({
            type: 'warning',
            text: 'server.error',
          });
        });
    },

    handleChangeExternalCheckoutAmount(data) {
      this.amount = data.amount;
      this.dealIds = data.dealIds;
      this.handleChangeInvalidAmount(data);
    },

    handleChangeCart() {
      this.isShownErrorPayout = false;
      this.$router.push({ name: 'cardSelect' });
    },

    handleChangeInvalidAmount(data) {
      if (normalizeAmount(data.amount, this.currency) === 0) {
        this.isInvalidAmount = false;
        this.isInvalidMinimalAmount = false;
        return;
      }

      if (data.minAmount > normalizeAmount(data.amount, this.currency)) {
        this.isInvalidAmount = true;
        this.isInvalidMinimalAmount = true;
        return;
      }

      this.isInvalidAmount = data.isInvalidAmount;
      this.isInvalidMinimalAmount = false;
    },
    ...mapActions('cards', [
      'fetchCardList',
      'fetchCardLimits',
    ]),
  },
};
</script>

<style lang="sass" module>
  .container
    +primary-container

  .title
    +flexbox
    +flex-direction(column)
    +header-container

    +respond-to('medium')
      +flex-direction(row)

  .text
    font-weight: 300
</style>

<style lang="sass" scoped>
  .error-title
    font-weight: 600
    font-size: 20px
    line-height: 30px

  .error-message
    font-weight: 500
    font-size: 14px
    line-height: 20px
    color: rgba(255, 255, 255, .75)

  .error-controls
    display: flex
    flex-direction: column

    +respond-to('small')
      flex-direction: row
      justify-content: space-between

  .error-control-active
    width: 100%
    margin-bottom: 10px

    +respond-to('small')
      width: 260px
      margin-bottom: 0

  .submit-button-container
    max-width: 340px
    margin: 30px auto 0
    padding: 0 20px

    +respond-to('medium')
      margin-top: 40px

  .action-header
    display: flex
    flex-flow: row wrap

    .action-title
      font-size: 1.42rem !important

    .title-subtext
      font-size: .85rem !important
      color: rgba(#fff, .5)

      span
        color: #fff
        font-size: 1.42rem !important
        white-space: nowrap

      @media (max-width: 480px)
        display: flex
        justify-content: space-between
        align-items: center
        width: 100%
        padding-top: 1.28rem
        text-align: left !important

      @media (min-width: 481px)
        margin-top: -1.42rem

        span
          display: block

  .commission-loader
    height: 22px

  .commission-text
    flex: 2
    display: flex
    flex-direction: column

  .commission-text-title
    font-family: Montserrat
    font-style: normal
    font-weight: 500
    font-size: 14px
    line-height: 24px
    color: rgba(255, 255, 255, 0.5)

    @media (max-width: 480px)
      font-size: 14px
      line-height: 22px

  .commission-text-content
    display: flex
    align-items: center
    font-family: Montserrat
    font-style: normal
    font-weight: 500
    font-size: 14px
    line-height: 22px
    color: rgba(255, 255, 255, 0.5)

    @media (max-width: 480px)
      font-size: 14px
      line-height: 22px

  .commission-currency
    flex: 1
    display: flex
    flex-direction: column
    align-items: flex-end
    font-family: Montserrat
    font-style: normal
    font-weight: 500
    font-size: 18px
    line-height: 22px
    color: #fff
    white-space: nowrap

    @media (max-width: 480px)
      font-weight: 600
      font-size: 14px
      line-height: 22px

  #buttonPayout

    button
      flex-direction: column
      font-weight: 600
      font-size: 18px
      line-height: 18px

      p
        font-size: 12px
        position: relative
        transition: 0.2s
        top: 0

      &:hover

        p
          top: -0.16rem

  .payoutInfo
    margin-top: 40px
    padding: 0 20px

    +respond-to('small')
      padding: 0

    &::v-deep
      p
        font-size: 16px

      a
        text-decoration: underline

        &:hover
          text-decoration: none

  .payout-less-container
    margin-top: 1.42rem

  $loading-size: 8px

  %loading
    width: $loading-size
    height: $loading-size
    position: absolute
    top: 50%
    right: 0
    transform: translateY(-50%)
    border-radius: $loading-size

  .commission
    margin-top: 10px
    position: relative
    display: flex
    align-items: center
    justify-content: space-between

    span.loading
      @extend %loading
      flex: 1
      margin-right: $loading-size * 2
      background-color: rgba(#fff, .5)

      &::before,
      &::after
        content: ''
        position: absolute
        @extend %loading

      &::before
        left: $loading-size * -2
        background-color: rgba(#fff, 1)

      &::after
        right: $loading-size * -2
        background-color: rgba(#fff, .25)
</style>
